import { AfterContentInit, Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { MainMapService } from '../../../shared/service/main-map.service';
import { DomUtilService } from '../../../shared/service/dom-util.service';
import { WidgetNotifyService } from '../../../shared/service/widget-notify.service';
import {EsriExpand, EsriReactiveUtils} from '../../../config/esri.module';
import { ConfigService } from '../../../config/config.service';
import * as noUiSlider from 'nouislider';
import wNumb from 'wnumb';
import { TranslateService } from '@ngx-translate/core';
import { BaseWidget } from '../base-widget/base-widget';

@Component({
    selector: 'masstab-slider',
    templateUrl: './massstab-slider.component.html',
    standalone: false
})
export class MassStabSliderComponent extends BaseWidget implements OnInit, AfterContentInit {
  // Reference to DOM element of this component
  @ViewChild('massStabSliderComponentRef', { static: true }) massStabSliderComponentRef: ElementRef;
  @ViewChild('sliderRef', { static: true }) sliderRef: ElementRef;

  private _zoomSlider: any;
  private _sliderNumberFormat: any;

  constructor(
    public mainMapService: MainMapService,
    public widgetNotifyService: WidgetNotifyService,
    private _domUtilService: DomUtilService,
    private _configService: ConfigService,
    private _translate: TranslateService
  ) {
    super(mainMapService, widgetNotifyService);
  }

  ngOnInit() {
    // get the map view
    const mapView: __esri.MapView = this.mainMapService.mapView;

    // create an expand instance
    const expandProperties: __esri.ExpandProperties = {
      view: mapView,
      id: 'MassStabSliderComponent',
      content: this.massStabSliderComponentRef.nativeElement,
      expandIcon: 'map',
      collapseIcon: 'chevrons-left',
    };
    this.expandWidget = new EsriExpand(expandProperties);
    // translate tooltip icon
    super.translateToolTipWidgetIcon(this.expandWidget, this._translate, 'WIDGET.SCALING.TOOLTIP');

    // add widget to the main map
    this.addWidgetToMainMap(mapView);

    this.initWidgetListeners();
  }

  ngAfterContentInit() {
    // listen on language change
    this._translate.onLangChange.subscribe(() => {
      super.translateToolTipWidgetIcon(
        this.expandWidget,
        this._translate,
        'WIDGET.SCALING.TOOLTIP'
      );
    });
    // create silder
    this._zoomSlider = this.sliderRef.nativeElement;
    this.createSlider();

    // listen to map-scale change
    EsriReactiveUtils.watch(
      () => this.mainMapService.mapView.scale,
      (newValue: number, oldValue: number) => {
        if (newValue !== oldValue) {
          // set map-scale to slider
          this._zoomSlider.noUiSlider.set(newValue);
        }
      }
    );

    // listen on slider change
    this._zoomSlider.noUiSlider.on('change', () => {
      // get scale from slider
      const scaleString: string = this._zoomSlider.noUiSlider.get();
      // set slider-scale to map
      this.mainMapService.mapView.scale = this._sliderNumberFormat.from(scaleString);
    });

    // listen on scale set
    this._zoomSlider.noUiSlider.on('set', () => {
      // get exact scale from map
      const scale: number = this.mainMapService.mapView.scale;
      const scaleString: string = this._sliderNumberFormat.to(scale);

      // override slider tooltip with exact scale
      this._domUtilService.overrideScaleTooltip(this.sliderRef, scaleString);
    });
  }

  /**
   * @name createSlider
   * @description Creates the scale slider
   */
  public createSlider() {
    // define slider number format
    this._sliderNumberFormat = wNumb({
      decimals: 0,
      thousand: "'",
      prefix: '1:',
    });

    // define range and start scale
    const range: any = this._configService.mainMapConfig.zoomSlider.range;
    const scale: number = this.mainMapService.mapView.scale;

    // create slider
    this._zoomSlider.style.width = '200px';
    this._zoomSlider.style.height = '5px';
    noUiSlider.create(this._zoomSlider, {
      start: scale,
      snap: true,
      tooltips: true,
      range: range,
      pips: {
        // Show a scale with the slider
        mode: 'positions',
        values: [0, 50, 100],
        density: 10,
        stepped: true,
        format: this._sliderNumberFormat,
      },
      format: this._sliderNumberFormat,
    });
  }

  /**
   * adds the new widget to a given map
   * @param mapView the map to add the widget to
   */
  private addWidgetToMainMap(mapView: any) {
    mapView.ui.add(this.expandWidget, 'bottom-left');
    this.mainMapService.registerWidget(this.expandWidget);
  }
}
